import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  Platform,
  useWindowDimensions,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import profileDetailStyles from '../theme/profileDetailStyles';
import api from '../services/api';
import {
  PROFILE_FOR_OPTIONS,
  GENDER_OPTIONS,
  DOB_TYPE_OPTIONS,
  HEIGHT_OPTIONS,
  RELIGION_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  FOOD_HABITS_OPTIONS,
  TIME_OPTIONS,
  BIRTH_STAR_OPTIONS,
  BIRTH_SIGN_OPTIONS,
  SKIN_COMPLEXION_OPTIONS,
  ANNUAL_INCOME_OPTIONS,
  MOTHER_TONGUE_OPTIONS,
} from '../constants/profileOptions';

const PersonalTabScreen = ({ memberId, isOwnProfile, isMediatorProfile, onEditStateChange }) => {
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  const scrollViewRef = useRef(null);
  const fieldPositions = useRef({});
  
  const [personalData, setPersonalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeDropdownField, setActiveDropdownField] = useState(null);
  const [activeDropdownOptions, setActiveDropdownOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadPersonalData();
  }, [memberId]);

  useEffect(() => {
    if (isEditMode && onEditStateChange && Object.keys(editData).length > 0) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: true,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  }, [editData, isEditMode]);

  const loadPersonalData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/${memberId}/personal`);
      const data = response.data?.data || response.data;
      setPersonalData(data);
    } catch (error) {
      console.error('Error loading personal data:', error);
      if (Platform.OS === 'web') {
        alert('Failed to load personal details');
      } else {
        Alert.alert('Error', 'Failed to load personal details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    const dataToEdit = { ...(personalData || {}) };
    // Format date of birth for editing
    if (dataToEdit.dateOfBirth) {
      dataToEdit.dateOfBirth = formatDateForEdit(dataToEdit.dateOfBirth);
    }
    setEditData(dataToEdit);
    setIsEditMode(true);
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: false,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
    setValidationErrors({});
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: false,
        hasUnsavedChanges: false,
        onSave: null,
        onCancel: null,
      });
    }
  };

  const getMandatoryFields = () => [
    { key: 'registrationFor', label: 'Profile For' },
    { key: 'gender', label: 'Gender' },
    { key: 'name', label: 'Name' },
    { key: 'surName', label: 'Surname' },
    { key: 'dateOfBirthType', label: 'Date of Birth Type' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'height', label: 'Height' },
    { key: 'weight', label: 'Weight' },
    { key: 'religion', label: 'Religion' },
    { key: 'motherTongue', label: 'Mother Tongue' },
    { key: 'maritalStatus', label: 'Marital Status' },
    { key: 'profession', label: 'Current Occupation' },
    { key: 'annualIncome', label: 'Annual Income' },
  ];

  const validateDateFormat = (dateString) => {
    if (!dateString || dateString.trim() === '') {
      return { isValid: true }; // Empty is okay
    }

    // Check if format matches DD/MM/YYYY
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateString.match(datePattern);
    
    if (!match) {
      return {
        isValid: false,
        errorMessage: 'Date of Birth must be in DD/MM/YYYY format (e.g., 15/08/1995)'
      };
    }

    const [, day, month, year] = match;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    // Validate ranges
    if (monthNum < 1 || monthNum > 12) {
      return {
        isValid: false,
        errorMessage: 'Invalid month. Month must be between 01 and 12.'
      };
    }

    if (dayNum < 1 || dayNum > 31) {
      return {
        isValid: false,
        errorMessage: 'Invalid day. Day must be between 01 and 31.'
      };
    }

    // Check if the date is valid (e.g., not 31/02/2000)
    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (date.getFullYear() !== yearNum || 
        date.getMonth() !== monthNum - 1 || 
        date.getDate() !== dayNum) {
      return {
        isValid: false,
        errorMessage: 'Invalid date. Please check the day and month combination.'
      };
    }

    // Check if date is not in the future
    const today = new Date();
    if (date > today) {
      return {
        isValid: false,
        errorMessage: 'Date of Birth cannot be in the future.'
      };
    }

    return { isValid: true };
  };

  const handleSave = async () => {
    // Validate mandatory fields
    const mandatoryFields = getMandatoryFields();
    const errors = {};

    mandatoryFields.forEach(field => {
      const value = field.key in editData ? editData[field.key] : personalData?.[field.key];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors[field.key] = `${field.label} is required`;
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Scroll to first error field
      if (scrollViewRef.current) {
        const firstErrorKey = Object.keys(errors)[0];
        const errorPosition = fieldPositions.current[firstErrorKey];
        if (errorPosition !== undefined) {
          scrollViewRef.current.scrollTo({ y: Math.max(0, errorPosition - 100), animated: true });
        } else {
          scrollViewRef.current.scrollTo({ y: 0, animated: true });
        }
      }
      
      if (Platform.OS === 'web') {
        alert('Please fill in all mandatory fields');
      } else {
        Alert.alert('Mandatory Fields Required', 'Please fill in all mandatory fields');
      }
      return false;
    }

    // Validate date of birth format before saving
    if (editData.dateOfBirth) {
      const dateValidation = validateDateFormat(editData.dateOfBirth);
      if (!dateValidation.isValid) {
        if (Platform.OS === 'web') {
          alert(dateValidation.errorMessage);
        } else {
          Alert.alert('Invalid Date Format', dateValidation.errorMessage);
        }
        return false;
      }
    }

    setIsSaving(true);
    try {
      const dataToSave = { ...editData };
      // Convert date of birth back to ISO format for API
      if (dataToSave.dateOfBirth) {
        dataToSave.dateOfBirth = convertDateToISO(dataToSave.dateOfBirth);
      }
      const response = await api.put(`/api/EnhancedMember/${memberId}/personal`, dataToSave);
      setPersonalData(response.data?.data || response.data);
      
      if (Platform.OS === 'web') {
        alert('Personal details updated successfully');
      } else {
        Alert.alert('Success', 'Personal details updated successfully');
      }
      
      setIsEditMode(false);
      setEditData({});
      setValidationErrors({});
      if (onEditStateChange) {
        onEditStateChange({
          isEditing: false,
          hasUnsavedChanges: false,
          onSave: null,
          onCancel: null,
        });
      }
      return true;
    } catch (error) {
      console.error('Error saving personal data:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save changes';
      
      if (Platform.OS === 'web') {
        alert(errorMessage);
      } else {
        Alert.alert('Error', errorMessage);
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatDateForEdit = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const convertDateToISO = (dateString) => {
    if (!dateString) return null;
    // If already in ISO format, return as is
    if (dateString.includes('T') || dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateString;
    }
    // Parse DD/MM/YYYY format
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return null;
  };

  const getOptionsForField = (fieldKey) => {
    const optionsMap = {
      registrationFor: PROFILE_FOR_OPTIONS,
      gender: GENDER_OPTIONS,
      dateOfBirthType: DOB_TYPE_OPTIONS,
      height: HEIGHT_OPTIONS,
      religion: RELIGION_OPTIONS,
      maritalStatus: MARITAL_STATUS_OPTIONS.filter(option => option.value !== 'Any'),
      foodHabits: FOOD_HABITS_OPTIONS,
      timeOfBirth: TIME_OPTIONS,
      birthStar: BIRTH_STAR_OPTIONS,
      birthSign: BIRTH_SIGN_OPTIONS,
      skinComplexion: SKIN_COMPLEXION_OPTIONS,
      annualIncome: ANNUAL_INCOME_OPTIONS,
      // motherTongue removed - now free text
    };
    return optionsMap[fieldKey] || null;
  };

  const openDropdown = (fieldKey, label) => {
    const options = getOptionsForField(fieldKey);
    if (options) {
      setActiveDropdownField({ key: fieldKey, label });
      setActiveDropdownOptions(options);
      setDropdownVisible(true);
    }
  };

  const selectDropdownOption = (value) => {
    if (activeDropdownField) {
      // Convert height value to string for API compatibility
      const finalValue = activeDropdownField.key === 'height' ? String(value) : value;
      
      setEditData({ ...editData, [activeDropdownField.key]: finalValue });
      // Clear error when user selects a value
      if (validationErrors[activeDropdownField.key]) {
        setValidationErrors({ ...validationErrors, [activeDropdownField.key]: undefined });
      }
    }
    setDropdownVisible(false);
    setActiveDropdownField(null);
  };

  const renderSectionTitle = (title) => (
    <Text style={profileDetailStyles.sectionHeader}>{title}</Text>
  );

  const renderInfoRow = (label, value, fieldKey, editable = true) => {
    const isEditing = isEditMode && editable;
    const options = getOptionsForField(fieldKey);
    const hasDropdown = options !== null;
    const isMandatory = getMandatoryFields().some(f => f.key === fieldKey);
    const hasError = validationErrors[fieldKey];
    const displayLabel = isEditing && isMandatory ? `${label} *` : label;
    
    // Determine keyboard type for numeric fields
    const isNumericField = fieldKey === 'weight';
    
    if (isEditing) {
      // Edit mode with floating label
      // Use editData value directly, no fallback to formatted value
      const editValue = fieldKey in editData ? editData[fieldKey] : value;
      
      return (
        <View 
          style={profileDetailStyles.inputWrapper}
          onLayout={(event) => {
            fieldPositions.current[fieldKey] = event.nativeEvent.layout.y;
          }}
        >
          <Text style={profileDetailStyles.floatingLabel}>{displayLabel}</Text>
          {hasDropdown ? (
            <TouchableOpacity
              style={[profileDetailStyles.dropdown, hasError && profileDetailStyles.inputError]}
              onPress={() => openDropdown(fieldKey, label)}
            >
              <Text style={(fieldKey in editData ? editData[fieldKey] : value) ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {fieldKey in editData ? (editData[fieldKey] || `Select ${label}`) : (value || `Select ${label}`)}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          ) : (
            <TextInput
              style={[
                profileDetailStyles.input,
                focusedInput === fieldKey && profileDetailStyles.inputFocused,
                hasError && profileDetailStyles.inputError
              ]}
              value={fieldKey in editData ? (editData[fieldKey]?.toString() || '') : (value?.toString() || '')}
              onChangeText={(text) => {
                // For numeric fields, only allow numbers and decimal point
                if (isNumericField) {
                  // Allow only numbers and one decimal point
                  const numericText = text.replace(/[^0-9.]/g, '');
                  // Prevent multiple decimal points
                  const parts = numericText.split('.');
                  const validText = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericText;
                  setEditData({ ...editData, [fieldKey]: validText });
                } else {
                  setEditData({ ...editData, [fieldKey]: text });
                }
                if (hasError) {
                  setValidationErrors({ ...validationErrors, [fieldKey]: undefined });
                }
              }}
              onFocus={() => setFocusedInput(fieldKey)}
              onBlur={() => setFocusedInput(null)}
              placeholder={fieldKey === 'dateOfBirth' ? 'DD/MM/YYYY' : `Enter ${label}`}
              placeholderTextColor={colors.textSecondary}
              keyboardType={isNumericField ? 'numeric' : 'default'}
            />
          )}
          {hasError && (
            <Text style={profileDetailStyles.errorText}>{hasError}</Text>
          )}
        </View>
      );
    } else {
      // Display mode
      return (
        <View style={profileDetailStyles.infoItem}>
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
            <View style={profileDetailStyles.viewModeContainer}>
              <Text style={profileDetailStyles.viewModeText}>{value || 'Not Provided'}</Text>
            </View>
          </View>
        </View>
      );
    }
  };

  if (isLoading) {
    return (
      <View style={profileDetailStyles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.secondary} />
        <Text style={profileDetailStyles.loadingText}>Loading personal details...</Text>
      </View>
    );
  }

  // Use personalData or empty object to allow editing even if no data exists
  const displayData = personalData || {};

  return (
    <View style={profileDetailStyles.container}>
      <ScrollView 
        ref={scrollViewRef}
        style={profileDetailStyles.scrollView} 
        contentContainerStyle={profileDetailStyles.scrollContent}
      >
        {renderSectionTitle('Basic Information')}
        {renderInfoRow('Profile For', displayData.registrationFor, 'registrationFor')}
        {renderInfoRow('Gender', displayData.gender, 'gender')}
        {renderInfoRow('Name', displayData.name, 'name')}
        {renderInfoRow('SurName', displayData.surName, 'surName')}

        {renderSectionTitle('Birth Information')}
        {renderInfoRow('Date of Birth Type', displayData.dateOfBirthType, 'dateOfBirthType')}
        {renderInfoRow('Date of Birth (DD/MM/YYYY)', formatDate(displayData.dateOfBirth), 'dateOfBirth')}
        {renderInfoRow('Time of Birth', displayData.timeOfBirth, 'timeOfBirth')}
        {renderInfoRow('Birth Star (Nakshatram)', displayData.birthStar, 'birthStar')}
        {renderInfoRow('Birth Sign (Raasi)', displayData.birthSign, 'birthSign')}
        {renderInfoRow('Place of Birth', displayData.placeOfBirth, 'placeOfBirth')}
        {renderInfoRow('Place of Birth District', displayData.placeOfBirthDistrict, 'placeOfBirthDistrict')}

        {renderSectionTitle('Physical Characteristics')}
        {renderInfoRow('Height (cm)', displayData.height, 'height')}
        {renderInfoRow('Weight (Kgs)', displayData.weight, 'weight')}
        {renderInfoRow('Skin Complexion', displayData.skinComplexion, 'skinComplexion')}

        {renderSectionTitle('Religious/Cultural Information')}
        {renderInfoRow('Religion', displayData.religion, 'religion')}
        {renderInfoRow('Caste (Kulam)', displayData.caste, 'caste')}
        {renderInfoRow('Sub Caste', displayData.subCaste, 'subCaste')}
        {renderInfoRow('Gotram', displayData.gotram, 'gotram')}
        {renderInfoRow('Shakha', displayData.shakha, 'shakha')}
        {renderInfoRow('Mother Tongue', displayData.motherTongue, 'motherTongue')}

        {renderSectionTitle('Status Information')}
        {renderInfoRow('Marital Status', displayData.maritalStatus, 'maritalStatus')}
        {renderInfoRow('Food Habits', displayData.foodHabits, 'foodHabits')}

        {renderSectionTitle('Professional Information')}
        {renderInfoRow('Current Occupation', displayData.profession, 'profession')}
        {renderInfoRow('Annual Income', displayData.annualIncome, 'annualIncome')}
        {renderInfoRow('Company Name and Full Address', displayData.companyNameAndAddress, 'companyNameAndAddress')}
      </ScrollView>

      {/* Floating Edit Buttons */}
      {(isOwnProfile || isMediatorProfile) && (
        <View style={profileDetailStyles.floatingButtonContainer}>
          {isEditMode ? (
            <>
              <TouchableOpacity 
                onPress={handleCancelEdit} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingCancelButton]}
                disabled={isSaving}
              >
                <SvgIcon name="close" size={20} color={colors.white} />
              </TouchableOpacity>
              <TouchableOpacity 
                onPress={handleSave} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingSaveButton]}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <SvgIcon name="checkmark" size={20} color={colors.white} />
                )}
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity 
              onPress={handleEdit} 
              style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingEditButton]}
            >
              <SvgIcon name="create-outline" size={20} color={colors.white} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Dropdown Modal */}
      <Modal
        visible={dropdownVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity 
          style={profileDetailStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={profileDetailStyles.modalContent}>
            <Text style={profileDetailStyles.modalTitle}>
              {activeDropdownField?.label || 'Select'}
            </Text>
            <ScrollView style={profileDetailStyles.modalScroll}>
              {activeDropdownOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={profileDetailStyles.modalItem}
                  onPress={() => selectDropdownOption(option.value)}
                >
                  <Text style={profileDetailStyles.modalItemText}>{option.label}</Text>
                  {(editData[activeDropdownField?.key] || personalData?.[activeDropdownField?.key]) === option.value && (
                    <SvgIcon name="checkmark-circle" size={20} color={colors.secondary} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// All styles now use profileDetailStyles from theme

export default PersonalTabScreen;
